import { Lifecycle } from '@shared/models/lifecycles.model';
import { SbefGroupCode } from '@shared/construction/sbef.model';
import { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';

import {
  ScopePreset,
  ScopeTarget,
  TargetBuildingType,
  KgCo2ePerM2GfaByBuildingType,
} from '@shared/models/scope-preset.model';
export * from '@shared/models/scope-preset.model';

// Byggdelar BD n map 1:1 to SBEF group code n (Boverket byggdelsindelning).
/**
 * BD 2–6 + 9: load-bearing structure and envelope. Common works (9) carries
 * shared costs and emissions that belong to every scope, so it is always in.
 */
const BD_2_6: SbefScopeGroupKey[] = [
  SbefGroupCode.HouseSubstructure,
  SbefGroupCode.Framework,
  SbefGroupCode.OuterRoof,
  SbefGroupCode.Facades,
  SbefGroupCode.FrameCompletionRoomForm,
  SbefGroupCode.CommonWorks,
];

/** BD 2–8 + 9: {@link BD_2_6} plus interior surface layers and installations. */
const BD_2_8: SbefScopeGroupKey[] = [
  ...BD_2_6,
  SbefGroupCode.InteriorSurfaceLayersRoomCompletion,
  SbefGroupCode.Installations,
];

/** A1–A5: product + construction stages. */
const A1_A5: Lifecycle[] = [Lifecycle.A1A3, Lifecycle.A4, Lifecycle.A5];

/**
 * Full lifecycle A–C. B3/B5 and D are not yet in the {@link Lifecycle} enum, so
 * "B1–B7" resolves to B1, B2, B4, B6, B7 and "C1–C4" to C1, C2, C3-C4.
 */
const FULL_LIFECYCLE_A_C: Lifecycle[] = [
  ...A1_A5,
  Lifecycle.B1,
  Lifecycle.B2,
  Lifecycle.B4,
  Lifecycle.B6,
  Lifecycle.B7,
  Lifecycle.C1,
  Lifecycle.C2,
  Lifecycle.C3C4,
];

const FULL_LIFECYCLE_PERIOD_YEARS = 50;

/**
 * Miljöbyggnad 4.1, indikator 4, tabell 4.1 — maximum climate impact for A1–A5.
 * Retail and industry have no row of their own and are graded as "Övriga".
 * Brons has no limit value, only a duty to declare, so it is not a target here.
 */
const MILJOBYGGNAD_SILVER: KgCo2ePerM2GfaByBuildingType = {
  [TargetBuildingType.MultiDwelling]: 290,
  [TargetBuildingType.Office]: 280,
  [TargetBuildingType.School]: 270,
  [TargetBuildingType.Other]: 370,
};

const MILJOBYGGNAD_GULD: KgCo2ePerM2GfaByBuildingType = {
  [TargetBuildingType.MultiDwelling]: 260,
  [TargetBuildingType.Office]: 250,
  [TargetBuildingType.School]: 240,
  [TargetBuildingType.Other]: 330,
};

/**
 * BREEAM-SE Nybyggnad v6.1, Mat 01, tabell 31 — referensvärden för
 * klimatpåverkan (A1–A5, enligt lagen om klimatdeklaration). BREEAM sets no
 * limit value of its own: points are awarded for a percentage improvement on
 * these, so each target below is the reference cut by its own percentage.
 *
 * The kontor value carries the table's footnote: it applies to every other
 * building covered by klimatdeklaration beyond the listed types, which is why
 * "Övriga" shares it rather than having a value of its own.
 */
const BREEAM_REFERENCE: KgCo2ePerM2GfaByBuildingType = {
  [TargetBuildingType.MultiDwelling]: 307,
  [TargetBuildingType.Office]: 307,
  [TargetBuildingType.School]: 297,
  [TargetBuildingType.Other]: 307,
};

/**
 * Mat 01, tabell 30 — procentuell förbättring och tilldelning av poäng.
 *
 * The exemplary level, a further 40% improvement, is not a target here: it also
 * demands points on Ene 01 and Ene 04, so reaching the carbon figure alone does
 * not earn it.
 */
const BREEAM_TARGETS: ScopeTarget[] = [
  // "Poäng" in the Swedish manual; credit is what BREEAM calls it in English,
  // which is the language of the interface.
  { id: 'breeam-1p', name: '1 credit', reduction: 0.1 },
  { id: 'breeam-2p', name: '2 credits', reduction: 0.2 },
  { id: 'breeam-3p', name: '3 credits', reduction: 0.3 },
].map(({ id, name, reduction }) => ({
  id,
  targetName: `${name} (${Math.round(reduction * 100)} %)`,
  shortTargetName: name,
  kind: 'reduction' as const,
  // Kriterium 3 compares against the klimatdeklaration scope, which is A1-A5 —
  // the full LCA in kriterium 2 earns its points by being done, not by passing
  // a limit.
  lifecycles: A1_A5,
  reduction,
}));

/**
 * Predefined LCA scope presets selectable from the scope selector (DEV-3539, Drop 1).
 * Applying one overwrites the persisted lifecycle, SBEF and conservative-carbon scope,
 * and — for full-lifecycle scopes — the project's calculation period.
 * Organization scopes are stored separately. Gränsvärde-based
 * certification levels are {@link ScopeTarget}s on the scope, not separate presets.
 */
export const SCOPE_PRESETS: readonly ScopePreset[] = [
  {
    id: 'boverket',
    label: 'Boverket',
    lifecycles: A1_A5,
    sbefGroups: BD_2_6,
    genericClimateData: 'conservative',
  },
  {
    id: 'boverket-typical',
    label: 'SBEF 2-6, typical',
    lifecycles: A1_A5,
    sbefGroups: BD_2_6,
    genericClimateData: 'typical',
  },
  {
    id: 'breeam-a1-a5',
    label: 'BREEAM-SE v6.1, embodied',
    lifecycles: A1_A5,
    sbefGroups: BD_2_6,
    genericClimateData: 'typical',
    targets: BREEAM_TARGETS,
    referenceValues: BREEAM_REFERENCE,
  },
  {
    id: 'breeam-full',
    label: 'BREEAM-SE v6.1, full lifecycle',
    lifecycles: FULL_LIFECYCLE_A_C,
    sbefGroups: BD_2_8,
    genericClimateData: 'typical',
    calculationPeriodYears: FULL_LIFECYCLE_PERIOD_YEARS,
    // Credits stay on the embodied scope. Full LCA is kriterium 2: points for
    // being done, not a carbon limit on A–C.
  },
  // Two scopes, because the levels differ in what has to be calculated, not only
  // in the limit value: Guld demands the whole lifecycle. Both limits grade
  // A1-A5 alone (tabell 4.1), which is what the embodied scope reports.
  {
    // Id kept from when the scope was named after its level: it is the key a
    // picked scope is persisted under.
    id: 'miljobyggnad-silver',
    label: 'Miljöbyggnad, embodied',
    lifecycles: A1_A5,
    sbefGroups: BD_2_6,
    genericClimateData: 'conservative',
    // Both levels grade A1-A5, which is exactly what this scope reports, so the
    // same scope can be read against either — Silver first, as the level a
    // project is held to unless it aims higher.
    targets: [
      {
        id: 'silver',
        targetName: 'Silver',
        kind: 'absolute',
        kgCo2ePerM2Gfa: MILJOBYGGNAD_SILVER,
      },
      {
        id: 'guld',
        targetName: 'Guld',
        kind: 'absolute',
        kgCo2ePerM2Gfa: MILJOBYGGNAD_GULD,
      },
    ],
  },
  {
    // Id kept from when the scope was named after its level, for the same
    // reason as the embodied one above.
    id: 'miljobyggnad-guld',
    label: 'Miljöbyggnad, full lifecycle',
    // Guld demands the whole lifecycle be calculated, though only A1-A5 is
    // graded against a limit value.
    lifecycles: FULL_LIFECYCLE_A_C,
    sbefGroups: BD_2_8,
    genericClimateData: 'typical',
    calculationPeriodYears: FULL_LIFECYCLE_PERIOD_YEARS,
    targets: [
      {
        id: 'guld',
        targetName: 'Guld',
        kind: 'absolute',
        lifecycles: A1_A5,
        kgCo2ePerM2Gfa: MILJOBYGGNAD_GULD,
      },
    ],
  },
];
